import React, { useEffect } from 'react';
import _ from 'underscore';
import { titleize, humanize } from 'underscore.string';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import { stateCodes } from 'components/modules/user/user-form-utils';
import { useTranslation } from 'react-i18next';
import 'utils/selectize';
import { sluggify } from '../../../../utils/localization/utils';
const REQUIRED_ATTRIBUTES = [
    'first_name',
    'last_name',
    'username',
    'phone',
    'email',
    'company_name',
    'street',
    'city',
    'state',
    'zipcode',
];
const INITIAL_ERROR_OBJECT = {
    first_name: {},
    last_name: {},
    username: {},
    phone: {},
    email: {},
    company_name: {},
    address: {},
};
const ADDRESS_FIELDS = ['street', 'street2', 'city', 'state', 'zipcode'];
const CONSIGNEE_FIELDS = [
    'first_name',
    'last_name',
    'username',
    'phone',
    'email',
    'company_name',
];
const getRequiredAsterisk = (field) => {
    return REQUIRED_ATTRIBUTES.includes(field) ? '*' : '';
};
const ConsigneeForm = ({ details, category, onSave }) => {
    var _a, _b, _c, _d, _e;
    const [errors, setErrors] = React.useState(INITIAL_ERROR_OBJECT);
    const [changed, setChanged] = React.useState({});
    const [consignee, setConsignee] = React.useState(details);
    const { t } = useTranslation();
    useEffect(() => {
        updateErrorObj();
    }, []);
    function updateErrorObj() {
        if (category === 'address') {
            areRequiredFieldsPresent(consignee.address, ADDRESS_FIELDS);
        }
        else {
            areRequiredFieldsPresent(consignee, CONSIGNEE_FIELDS);
        }
    }
    const areRequiredFieldsPresent = (parent, fields) => {
        const errorsObj = errors;
        fields.forEach((field) => {
            errorsObj[field] =
                !parent[field] && REQUIRED_ATTRIBUTES.includes(field)
                    ? { message: t('FIELD_REQUIRED_TIP', { field: titleize(t(sluggify(field))) }) }
                    : {};
        });
        setErrors(errorsObj);
        const allPresent = _.every(fields, (f) => _.isEmpty(errors[f]));
        return allPresent;
    };
    const handleAddressFieldChange = (field, value) => {
        const address = consignee.address;
        address[field] = value;
        setConsignee((consignee) => (Object.assign(Object.assign({}, consignee), { address })));
        setChanged({ address: true });
        const errorsObj = Object.assign({}, errors);
        errorsObj[field] =
            !value && REQUIRED_ATTRIBUTES.includes(field)
                ? {
                    message: t('FIELD_REQUIRED_TIP', { field: titleize(t(sluggify(field))) }),
                }
                : {};
        setErrors(errorsObj);
    };
    const handleFieldChange = (field, value) => {
        setConsignee((consignee) => (Object.assign(Object.assign({}, consignee), { [field]: value })));
        setChanged((changed) => (Object.assign(Object.assign({}, changed), { [field]: true })));
        const errorsObj = Object.assign({}, errors);
        errorsObj[field] =
            !value && REQUIRED_ATTRIBUTES.includes(field)
                ? {
                    message: t('FIELD_REQUIRED_TIP', { field: titleize(t(sluggify(field))) }),
                }
                : {};
        setErrors(errorsObj);
    };
    const saveDetails = () => {
        if (category === 'address') {
            if (!areRequiredFieldsPresent(consignee.address, ADDRESS_FIELDS)) {
                return;
            }
        }
        else if (!areRequiredFieldsPresent(consignee, CONSIGNEE_FIELDS)) {
            return;
        }
        _.map(changed, (item, key) => {
            if (key === 'address') {
                const address = consignee.address;
                _.map(consignee.address, (item, key) => {
                    address[key] = address[key].trim();
                });
                consignee.address = address;
            }
        });
        const updates = _.pick(consignee, Object.keys(changed));
        updates._id = consignee._id;
        onSave(updates);
    };
    function errorsExist() {
        const errorsObj = errors;
        let errorsExist = false;
        _.map(errorsObj, (value, key) => {
            if (!_.isEmpty(errorsObj[key]))
                errorsExist = true;
        });
        return errorsExist;
    }
    return (React.createElement("div", null,
        category === 'account' && (React.createElement("div", { className: "grid sm:grid-cols-6 sm:gap-4 gap-y-6" },
            React.createElement(CargomaticInput, { className: "sm:col-span-6", error: errors.company_name, label: `${t('COMPANY_NAME')} ${getRequiredAsterisk('company_name')}`, info: t('CONSIGNEE_COMPANY_NAME'), onChange: (value) => handleFieldChange('company_name', value), value: consignee === null || consignee === void 0 ? void 0 : consignee.company_name }),
            React.createElement(CargomaticInput, { className: "sm:col-span-3", error: errors.first_name, label: `${t('FIRST_NAME')}  ${getRequiredAsterisk('first_name')}`, info: t('CONSIGNEE_FIRST_NAME'), onChange: (value) => handleFieldChange('first_name', value), value: consignee === null || consignee === void 0 ? void 0 : consignee.first_name }),
            React.createElement(CargomaticInput, { className: "sm:col-span-3", error: errors.last_name, label: `${t('LAST_NAME')} ${getRequiredAsterisk('last_name')}`, info: t('CONSIGNEE_LAST_NAME'), onChange: (value) => handleFieldChange('last_name', value), value: consignee === null || consignee === void 0 ? void 0 : consignee.last_name }),
            React.createElement(CargomaticInput, { className: "sm:col-span-3", error: errors.phone, label: `${t('PHONE_NUMBER')} ${getRequiredAsterisk('phone')}`, info: t('CONSIGNEE_PHONE_NUMBER'), onChange: (value) => handleFieldChange('phone', value), value: consignee === null || consignee === void 0 ? void 0 : consignee.phone }),
            React.createElement(CargomaticInput, { className: "sm:col-span-3", error: errors.username, label: `${t('USERNAME')} ${getRequiredAsterisk('username')}`, info: t('CONSIGNEE_USERNAME'), onChange: (value) => handleFieldChange('username', value), value: consignee === null || consignee === void 0 ? void 0 : consignee.username }),
            React.createElement(CargomaticInput, { className: "sm:col-span-4", error: errors.email, label: `${t('EMAIL_ADDRESS')} ${getRequiredAsterisk('email')}`, info: t('CONSIGNEE_EMAIL_ADDRESS'), onChange: (value) => handleFieldChange('email', value), value: consignee === null || consignee === void 0 ? void 0 : consignee.email, maxLength: 254 }))),
        category === 'address' && (React.createElement("div", null,
            React.createElement("h3", { className: "mt-0 mb-4" }, t('ADDRESS_INFO')),
            React.createElement("div", { className: "grid sm:grid-cols-3 sm:gap-4 gap-y-6" },
                React.createElement(CargomaticInput, { className: "sm:col-span-full", error: errors.street, label: `${titleize(t('STREET'))} ${getRequiredAsterisk('street')}`, info: t('CONSIGNEE_STREET'), onChange: (value) => {
                        handleAddressFieldChange('street', value);
                    }, value: (_a = consignee === null || consignee === void 0 ? void 0 : consignee.address) === null || _a === void 0 ? void 0 : _a.street }),
                React.createElement(CargomaticInput, { className: "sm:col-span-full", error: errors.street2, label: `${titleize(t('STREET_2'))} ${getRequiredAsterisk('street2')}`, info: t('CONSIGNEE_STREET_2'), onChange: (value) => {
                        handleAddressFieldChange('street2', value);
                    }, value: (_b = consignee === null || consignee === void 0 ? void 0 : consignee.address) === null || _b === void 0 ? void 0 : _b.street2 }),
                React.createElement(CargomaticInput, { error: errors.city, label: `${titleize(t('CITY'))} ${getRequiredAsterisk('city')}`, info: t('CONSIGNEE_CITY'), onChange: (value) => {
                        handleAddressFieldChange('city', value);
                    }, value: (_c = consignee === null || consignee === void 0 ? void 0 : consignee.address) === null || _c === void 0 ? void 0 : _c.city }),
                React.createElement(CargomaticSelect, { error: errors.state, selected: (_d = consignee === null || consignee === void 0 ? void 0 : consignee.address) === null || _d === void 0 ? void 0 : _d.state, info: t('CONSIGNEE_STATE'), items: stateCodes, label: `${titleize(t('STATE'))} ${getRequiredAsterisk('state')}`, valueKey: "abbreviation", textKey: "name", onSelect: (item) => {
                        handleAddressFieldChange('state', item.abbreviation);
                    } }),
                React.createElement(CargomaticInput, { error: errors.zipcode, label: `${t('ZIP_CODE')} ${getRequiredAsterisk('zipcode')}`, info: t('CONSIGNEE_POSTAL_CODE'), onChange: (value) => {
                        handleAddressFieldChange('zipcode', value);
                    }, value: (_e = consignee === null || consignee === void 0 ? void 0 : consignee.address) === null || _e === void 0 ? void 0 : _e.zipcode })))),
        React.createElement("div", { className: "my-2 form-actions" },
            React.createElement("button", { className: "button button-green", onClick: saveDetails, disabled: errorsExist() }, t('SAVE')))));
};
export default ConsigneeForm;
